import type { DishAllergen } from "api-types";

import { type MenuTreeNode, isValidGuestGroupHeader } from "./menu-tree";

/**
 * What a dish that offers choices is allowed to claim about its allergens.
 *
 * A dish record only carries its own base. Everything the guest is about to
 * pick lives in the children, so reading the parent alone under-reports, which
 * is how a waiter tells a coeliac the build-your-own burger is fine. Three
 * bands instead of one list:
 *
 *   always : nothing the guest picks can avoid it
 *   depends: some choice, or some removal, changes the answer
 *   may contain: cross-contact, its own axis, never merged into the others
 *
 * The rule that earns the file: a required group whose options ALL carry an
 * allergen makes that allergen unavoidable. Three sauces, egg in every one, and
 * the guest is offered a choice they do not have.
 *
 * Absent is not the same as unreadable. The menus service filters
 * `dishesInDisplayOrder` for hidden dishes, and for unverified ones once the
 * restaurant is verified, while passing `itemsInDisplayOrder` through untouched
 * (`createMenuSectionSchema`). A dish reference that does not resolve is
 * therefore the ordinary result of a restaurant hiding a dish, and stays
 * silent. `unreadable` is kept for structure we genuinely cannot describe: a
 * broken header record, or a dish nested under a dish with nothing stating how
 * the two are ordered together. Alarming on a hidden dish would put three red
 * rows on the menu of any restaurant with three seasonal dishes off, and spend
 * the signal that is supposed to mean something.
 */
export type AllergenBands = {
  always: DishAllergen[];
  depends: DishAllergen[];
  mayContain: DishAllergen[];
  /**
   * Where an allergen sits, for display. The three above are the other axis,
   * what the guest can change, and are what `verdictForSelection` reads.
   *
   * `inDish`: the dish's own record including `isOptional`, plus anything a
   * required group makes unavoidable. Empty for a header node.
   */
  inDish: DishAllergen[];
  inDishMayContain: DishAllergen[];
  /**
   * Reachable through a child group. Overlaps `inDish` by design. Excludes
   * anything a required group promoted to `always`.
   */
  inOptions: DishAllergen[];
  inOptionsMayContain: DishAllergen[];
  /**
   * Header nodes only: reach across the group's own options. Counts what each
   * option carries, `isOptional` included, so an allergen every option can drop
   * still reads as being in every one. `always` cannot answer this because it
   * drops optional allergens, which is a different question.
   */
  inEveryOption: DishAllergen[];
  inSomeOptions: DishAllergen[];
  /**
   * Allergen ids that can be removed outright: flagged optional on a dish, or
   * reachable only through a declinable group. Distinct from the rest of
   * `depends`, which needs a different option selected instead.
   */
  removable: Set<string>;
  /**
   * Why an allergen is unavoidable, when the reason is a group rather than the
   * dish itself. "Egg is in every sauce" is the fact the tree knows and a menu
   * does not show.
   */
  unavoidableFrom: { allergenName: string; groupTitle: string }[];
  /** Options and groups we could not read. Never zero silently. */
  unreadable: number;
};

type AllergenMap = Map<string, DishAllergen>;

type Bands = {
  always: AllergenMap;
  /** Always plus depends, before the two are separated. */
  all: AllergenMap;
  mayContain: AllergenMap;
  removable: Set<string>;
  /**
   * Ids that turn up somewhere nobody can decline: on the dish itself, or in a
   * required group. An allergen in both an optional extra and a required choice
   * is not something the kitchen leaves out, it is something you swap.
   */
  unremovable: Set<string>;
  inDish: AllergenMap;
  inDishMayContain: AllergenMap;
  inOptions: AllergenMap;
  inOptionsMayContain: AllergenMap;
  inEveryOption: AllergenMap;
  unavoidableFrom: { allergenName: string; groupTitle: string }[];
  unreadable: number;
};

const emptyBands = (): Bands => ({
  always: new Map(),
  all: new Map(),
  mayContain: new Map(),
  removable: new Set(),
  unremovable: new Set(),
  inDish: new Map(),
  inDishMayContain: new Map(),
  inOptions: new Map(),
  inOptionsMayContain: new Map(),
  inEveryOption: new Map(),
  unavoidableFrom: [],
  unreadable: 0,
});

const put = (
  into: AllergenMap,
  allergens: DishAllergen[] | undefined
): void => {
  for (const allergen of allergens ?? []) {
    if (allergen && !into.has(allergen.igfdTaxAllergenId)) {
      into.set(allergen.igfdTaxAllergenId, allergen);
    }
  }
};

const merge = (into: AllergenMap, from: AllergenMap): void => {
  for (const [id, allergen] of from) {
    if (!into.has(id)) into.set(id, allergen);
  }
};

const intersect = (a: AllergenMap, b: AllergenMap): AllergenMap => {
  const shared: AllergenMap = new Map();
  for (const [id, allergen] of a) {
    if (b.has(id)) shared.set(id, allergen);
  }
  return shared;
};

const byName = (map: AllergenMap): DishAllergen[] =>
  [...map.values()].sort((a, b) =>
    a.allergenName.localeCompare(b.allergenName)
  );

const dishBands = (node: MenuTreeNode): Bands => {
  const bands = emptyBands();
  // Withheld by the response, not broken. It is not on the menu, so it carries
  // nothing and warns about nothing.
  if (!node.dish) return bands;

  put(
    bands.always,
    node.dish.allergens?.filter((allergen) => !allergen.isOptional)
  );
  put(bands.all, node.dish.allergens);
  put(bands.mayContain, node.dish.mayContainAllergens);
  put(bands.inDish, node.dish.allergens);
  put(bands.inDishMayContain, node.dish.mayContainAllergens);
  // Flagged optional on the dish: removable outright, no alternative needed.
  for (const allergen of node.dish.allergens ?? []) {
    if (!allergen) continue;
    if (allergen.isOptional) bands.removable.add(allergen.igfdTaxAllergenId);
    else bands.unremovable.add(allergen.igfdTaxAllergenId);
  }

  for (const child of node.children) {
    // A dish can only own choice groups. A bare dish under a dish has no
    // stated relationship, so we cannot say whether it is ordered with it.
    if (child.kind !== "header") {
      bands.unreadable += 1;
      continue;
    }

    // Read the group either way. A header we cannot parse still has options
    // underneath whose allergens we can read, and dropping them would put a
    // known peanut out of reach of every surface.
    const group = groupBands(child);
    merge(bands.all, group.all);
    merge(bands.mayContain, group.mayContain);
    merge(bands.inOptions, group.all);
    merge(bands.inOptionsMayContain, group.mayContain);
    bands.unreadable += group.unreadable;

    const header = isValidGuestGroupHeader(child.header) ? child.header : null;
    if (!header) {
      // We cannot tell whether this group can be declined, so nothing it
      // contributes may be promised as removable, including an occurrence the
      // dish itself marked optional.
      bands.unreadable += 1;
      for (const [id] of group.all) bands.unremovable.add(id);
      continue;
    }

    if (!header.required) {
      // Declining the whole group removes everything only it contributes.
      // Nothing propagates from a group we cannot read: taking its optional
      // flags without its mandatory ones starred an allergen one option
      // carries outright.
      for (const [id] of group.all) bands.removable.add(id);
      continue;
    }
    // Reasons only travel up a path the guest cannot decline. Pushed before
    // this guard, a group inside an optional side was named as the reason the
    // dish itself could not avoid something.
    bands.unavoidableFrom.push(...group.unavoidableFrom);
    // A required group is a choice, not an omission: everything reachable
    // through it has to be swapped rather than left out. Requiredness is the
    // group's property, so an option's own record does not travel up past a
    // group the guest can decline.
    for (const [id] of group.all) bands.unremovable.add(id);
    for (const id of group.unremovable) bands.unremovable.add(id);

    // The group names itself for what all its options share, so there is
    // nothing to re-explain here.
    merge(bands.always, group.always);
  }

  // A group cannot explain what the dish carries in its own right. Reasons
  // bubble up from any depth, so one can arrive naming a group the guest need
  // never meet, for an allergen the dish declares in its own right.
  const ownStuck = new Set(
    (node.dish.allergens ?? [])
      .filter((allergen) => allergen && !allergen.isOptional)
      .map((allergen) => allergen.allergenName)
  );
  bands.unavoidableFrom = bands.unavoidableFrom.filter(
    (reason) => !ownStuck.has(reason.allergenName)
  );

  return bands;
};

const groupBands = (node: MenuTreeNode): Bands => {
  const bands = emptyBands();
  const title = isValidGuestGroupHeader(node.header) ? node.header.title : "";
  // No empty-group check: the adapter prunes a valid group with nothing under
  // it, so one arriving empty has a broken header, and the caller counts that
  // once. Counting it here too would report the same fault twice.

  // Null until the first readable option: an empty intersection and "no options
  // counted yet" are different, and only one of them means nothing is shared.
  let sharedByOptions: AllergenMap | null = null;
  // Same intersection over what each option carries rather than what it cannot
  // avoid, so an allergen every option can drop still reads as in every one.
  let carriedByEveryOption: AllergenMap | null = null;
  const fromRequiredNested: AllergenMap = new Map();
  const reachFromRequiredNested: AllergenMap = new Map();

  for (const child of node.children) {
    if (child.kind === "dish") {
      const option = dishBands(child);
      merge(bands.all, option.all);
      merge(bands.mayContain, option.mayContain);
      bands.unreadable += option.unreadable;
      for (const id of option.removable) bands.removable.add(id);
      for (const id of option.unremovable) bands.unremovable.add(id);
      // An option we cannot read must not narrow the intersection. We do not
      // know what it carries, and letting it drop an allergen down to "depends"
      // would tell a waiter it can be chosen away.
      if (!child.dish) continue;
      sharedByOptions =
        sharedByOptions === null
          ? new Map(option.always)
          : intersect(sharedByOptions, option.always);
      const carried: AllergenMap = new Map(option.inDish);
      merge(carried, option.always);
      carriedByEveryOption =
        carriedByEveryOption === null
          ? carried
          : intersect(carriedByEveryOption, carried);
      continue;
    }

    // Every option in this group is ordered along with its required nested
    // groups, so what those always carry, this group always carries. Read even
    // a broken one: its options' allergens are still readable.
    const nested = groupBands(child);
    merge(bands.all, nested.all);
    merge(bands.mayContain, nested.mayContain);
    bands.unreadable += nested.unreadable;

    const header = isValidGuestGroupHeader(child.header) ? child.header : null;
    if (!header) {
      bands.unreadable += 1;
      for (const [id] of nested.all) bands.unremovable.add(id);
      continue;
    }
    if (!header.required) {
      for (const [id] of nested.all) bands.removable.add(id);
      continue;
    }
    bands.unavoidableFrom.push(...nested.unavoidableFrom);
    for (const [id] of nested.all) bands.unremovable.add(id);
    for (const id of nested.unremovable) bands.unremovable.add(id);
    for (const [id, allergen] of nested.always) {
      bands.unavoidableFrom.push({
        allergenName: allergen.allergenName,
        groupTitle: header.title,
      });
    }
    merge(fromRequiredNested, nested.always);
    merge(reachFromRequiredNested, nested.inEveryOption);
  }

  // What every option carries is this group's doing, so it explains itself.
  // Reasons from inside one option are not propagated: an option is the thing
  // a guest selects away, so naming a group only it owns points at a choice
  // most orders never reach.
  if (sharedByOptions) {
    for (const [, allergen] of sharedByOptions) {
      bands.unavoidableFrom.push({
        allergenName: allergen.allergenName,
        groupTitle: title,
      });
    }
    merge(bands.always, sharedByOptions);
  }
  merge(bands.always, fromRequiredNested);
  merge(bands.all, bands.always);
  if (carriedByEveryOption) merge(bands.inEveryOption, carriedByEveryOption);
  merge(bands.inEveryOption, reachFromRequiredNested);
  return bands;
};

/**
 * Where one option stands against the allergens a waiter has selected.
 *
 * "Contains" used to cover both an allergen nothing can shift and one the
 * kitchen simply leaves out, which are different answers to the guest: one ends
 * the conversation and the other is an order note.
 */
export type OptionVerdict =
  | "suitable"
  | "can-be-removed"
  | "depends-on-choice"
  | "not-suitable"
  | "may-contain";

export const verdictForSelection = (
  bands: AllergenBands,
  selectedAllergenIds: string[]
): OptionVerdict => {
  const selected = new Set(selectedAllergenIds);
  const carries = (list: DishAllergen[]) =>
    list.some((allergen) => selected.has(allergen.igfdTaxAllergenId));

  // Nothing about this option shifts it.
  if (carries(bands.always)) return "not-suitable";

  // Avoidable, but the two ways of avoiding it are different sentences at the
  // table. "Can be removed" is an order note the kitchen acts on; an allergen
  // reachable only through a required group of this option's own has to be
  // chosen around instead, and telling a waiter it can be left out is how a
  // guest ends up served the thing they asked to avoid.
  const offendingDepends = bands.depends.filter((allergen) =>
    selected.has(allergen.igfdTaxAllergenId)
  );
  if (offendingDepends.length > 0) {
    return offendingDepends.every((allergen) =>
      bands.removable.has(allergen.igfdTaxAllergenId)
    )
      ? "can-be-removed"
      : "depends-on-choice";
  }

  if (carries(bands.mayContain)) return "may-contain";
  return "suitable";
};
/**
 * Suitable first, then anything with only a cross-contact risk, then what
 * carries the thing. A waiter reads down until they find a tick, so the ticks
 * belong at the top. Stable inside each band, so the restaurant's running order
 * survives, and untouched with nothing selected.
 */
export const sortOptionsBySuitability = (
  nodes: MenuTreeNode[],
  selectedAllergenIds: string[],
  /**
   * Verdicts the caller has already worked out, by node id. `allergenBands`
   * walks a whole subtree, and the group above this needs the same numbers for
   * its own count, so without this they get computed twice per render.
   */
  verdicts?: Map<string, OptionVerdict>
): MenuTreeNode[] => {
  if (selectedAllergenIds.length === 0) return nodes;

  // Ordered by what a waiter can do about it: nothing, an order note, a further
  // pick, then the two they cannot fix by choosing.
  const rank: Record<OptionVerdict, number> = {
    suitable: 0,
    "can-be-removed": 1,
    "depends-on-choice": 2,
    "may-contain": 3,
    "not-suitable": 4,
  };

  // Only the options move, and only into slots options already held. A nested
  // group is a further step rather than a choice, so ranking it alongside them
  // floated "Choose your flavour" above the three wing sizes it comes after.
  const slots: number[] = [];
  nodes.forEach((node, index) => {
    if (node.kind === "dish") slots.push(index);
  });

  const ordered = slots
    .map((index) => ({
      node: nodes[index],
      index,
      rank: nodes[index].dish
        ? rank[
            verdicts?.get(nodes[index].id) ??
              verdictForSelection(
                allergenBands(nodes[index]),
                selectedAllergenIds
              )
          ]
        : 0,
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index);

  const out = [...nodes];
  slots.forEach((slot, position) => {
    out[slot] = ordered[position].node;
  });
  return out;
};

export const allergenBands = (node: MenuTreeNode): AllergenBands => {
  const isGroup = node.kind === "header";
  const bands = isGroup ? groupBands(node) : dishBands(node);

  // Unavoidable allergens belong with the dish's own, not under the options.
  const inDish: AllergenMap = new Map(bands.inDish);
  if (!isGroup) merge(inDish, bands.always);

  const removable = new Set(
    [...bands.removable].filter(
      (id) => !bands.always.has(id) && !bands.unremovable.has(id)
    )
  );

  // Cross-contact yields to a carried report, but only to one that cannot be
  // removed. Removing an ingredient does not remove the risk of contact with
  // it, so suppressing on a removable report printed "can be made without"
  // over a risk that remains.
  const stuck = (ids: Iterable<string>) =>
    new Set([...ids].filter((id) => !removable.has(id)));
  const stuckInDish = stuck(inDish.keys());
  const stuckAnywhere = stuck([...inDish.keys(), ...bands.inOptions.keys()]);
  const onlyRisked = (map: AllergenMap, alreadyCarried: Set<string>) =>
    byName(map).filter(
      (allergen) => !alreadyCarried.has(allergen.igfdTaxAllergenId)
    );

  return {
    always: byName(bands.always),
    depends: byName(bands.all).filter(
      (allergen) => !bands.always.has(allergen.igfdTaxAllergenId)
    ),
    mayContain: byName(bands.mayContain),
    inDish: byName(inDish),
    inDishMayContain: onlyRisked(bands.inDishMayContain, stuckInDish),
    // A second chip here would contradict the unavoidable reason line.
    inEveryOption: byName(bands.inEveryOption),
    inSomeOptions: byName(bands.all).filter(
      (allergen) => !bands.inEveryOption.has(allergen.igfdTaxAllergenId)
    ),
    inOptions: byName(bands.inOptions).filter(
      (allergen) =>
        bands.inDish.has(allergen.igfdTaxAllergenId) ||
        !bands.always.has(allergen.igfdTaxAllergenId)
    ),
    inOptionsMayContain: onlyRisked(bands.inOptionsMayContain, stuckAnywhere),
    // Removable only where every occurrence can be declined. An allergen in an
    // optional extra AND a required choice is swapped, not left out.
    removable,
    unavoidableFrom: bands.unavoidableFrom,
    unreadable: bands.unreadable,
  };
};

export type CardScope = "dish" | "group";

/** One chip as it renders: the allergen, the mark, and which glyph it takes. */
export type CardChip = {
  allergen: DishAllergen;
  removable: boolean;
  mayContain: boolean;
};

/**
 * The four chip rows a card renders, per scope, with the removable mark already
 * decided. The component renders these verbatim so the invariant tests check
 * what actually ships rather than a copy of the rule.
 *
 * Cross-contact rows are never removable: `removable` is keyed by allergen id
 * with no notion of which occurrence, and a trace risk is not something a
 * removable. Removable chips sort last so the faded ones form a block.
 */
export const cardBands = (
  bands: AllergenBands,
  scope: CardScope
): {
  carried: CardChip[];
  mayContain: CardChip[];
  options: CardChip[];
  optionsMayContain: CardChip[];
  reasons: { allergenName: string; groupTitle: string }[];
} => {
  const row = (allergens: DishAllergen[], carried: boolean): CardChip[] =>
    allergens
      .map((allergen) => ({
        allergen,
        removable: carried && bands.removable.has(allergen.igfdTaxAllergenId),
        mayContain: !carried,
      }))
      .sort((a, b) => Number(a.removable) - Number(b.removable));

  // Keyed to `always`, never to a rendered band: the carried row also holds
  // removable allergens, which must not read as unavoidable.
  const unavoidable = new Set(bands.always.map((a) => a.allergenName));
  const reasons = [
    ...new Map(
      bands.unavoidableFrom
        .filter((reason) => unavoidable.has(reason.allergenName))
        .map((reason) => [reason.allergenName, reason])
    ).values(),
  ];

  if (scope === "group") {
    // Only what every option carries outranks a trace. Subtracting
    // `inSomeOptions` too deleted the trace an option contributes when a
    // different option happens to carry the same allergen outright.
    const stated = new Set(
      bands.inEveryOption.map((allergen) => allergen.igfdTaxAllergenId)
    );
    return {
      carried: row(bands.inEveryOption, true),
      mayContain: row(
        bands.mayContain.filter(
          (allergen) => !stated.has(allergen.igfdTaxAllergenId)
        ),
        false
      ),
      options: row(bands.inSomeOptions, true),
      optionsMayContain: [],
      reasons,
    };
  }

  return {
    carried: row(bands.inDish, true),
    mayContain: row(bands.inDishMayContain, false),
    options: row(bands.inOptions, true),
    optionsMayContain: row(bands.inOptionsMayContain, false),
    reasons,
  };
};
